import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Loader2, Mail, Lock, User, Phone } from "lucide-react";
import { toast } from "sonner";
// Google OAuth temporarily disabled
// import { signInWithGoogle } from "@/lib/supabase-auth";

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "login" | "signup";
}

export function AuthModal({ open, onOpenChange, defaultTab = "login" }: AuthModalProps) {
  const { signIn, signUp, resetPassword, loading } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup form state
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");
  const [signupFullName, setSignupFullName] = useState("");
  const [signupPhone, setSignupPhone] = useState("");

  // Forgot password form state
  const [forgotEmail, setForgotEmail] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginEmail || !loginPassword) {
      toast.error("Please enter email and password");
      return;
    }

    try {
      await signIn({ email: loginEmail, password: loginPassword });

      // Close modal first
      onOpenChange(false);

      // Show welcome toast
      toast.success("Welcome back! Redirecting to your profile...");

      // Reset form
      setLoginEmail("");
      setLoginPassword("");

      // Redirect to profile page after a short delay
      setTimeout(() => {
        navigate("/profile");
      }, 500);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to login");
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signupEmail || !signupPassword || !signupFullName) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (signupPassword !== signupConfirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (signupPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await signUp({
        email: signupEmail,
        password: signupPassword,
        full_name: signupFullName,
        phone: signupPhone || undefined,
        preferred_language: isRTL ? 'ar' : 'en',
      });

      // Close modal first
      onOpenChange(false);

      // Show welcome toast
      toast.success("🎉 Welcome to AAAprinters! Redirecting to your profile...");

      // Reset form
      setSignupEmail("");
      setSignupPassword("");
      setSignupConfirmPassword("");
      setSignupFullName("");
      setSignupPhone("");

      // Redirect to profile page after a short delay
      setTimeout(() => {
        navigate("/profile");
      }, 500);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create account");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }

    try {
      await resetPassword(forgotEmail);
      toast.success("Password reset link sent to your email!");
      setForgotEmail("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send reset link");
    }
  };

  // Google Sign-In temporarily disabled - OAuth not configured in Supabase
  // const handleGoogleSignIn = async () => {
  //   try {
  //     await signInWithGoogle();
  //     toast.success("Redirecting to Google...");
  //   } catch (error) {
  //     toast.error(error instanceof Error ? error.message : "Failed to sign in with Google");
  //   }
  // };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow bg-clip-text text-transparent">
            {activeTab === "login" ? "Welcome Back!" : activeTab === "signup" ? "Join AAAprinters" : "Reset Password"}
          </DialogTitle>
          <DialogDescription>
            {activeTab === "login"
              ? "Sign in to your account to continue shopping"
              : activeTab === "signup"
              ? "Create an account to start shopping"
              : "Enter your email to receive a password reset link"}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          {/* LOGIN TAB */}
          <TabsContent value="login" className="space-y-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <div className="relative">
                  <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <div className="relative">
                  <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="link"
                className="p-0 h-auto text-sm"
                onClick={() => setActiveTab("forgot")}
              >
                Forgot password?
              </Button>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white"
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Sign In
              </Button>
            </form>

            {/* Google Sign-In temporarily disabled - OAuth not configured in Supabase */}
            {/* <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <Chrome className="mr-2 h-4 w-4" />
              Google
            </Button> */}
          </TabsContent>

          {/* SIGNUP TAB */}
          <TabsContent value="signup" className="space-y-4">
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-name">Full Name *</Label>
                <div className="relative">
                  <User className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="John Doe"
                    value={signupFullName}
                    onChange={(e) => setSignupFullName(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-email">Email *</Label>
                <div className="relative">
                  <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-phone">Phone (Optional)</Label>
                <div className="relative">
                  <Phone className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="signup-phone"
                    type="tel"
                    placeholder="+973-12345678"
                    value={signupPhone}
                    onChange={(e) => setSignupPhone(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">Password *</Label>
                <div className="relative">
                  <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                    required
                    minLength={6}
                  />
                </div>
                <p className="text-xs text-muted-foreground">At least 6 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-confirm-password">Confirm Password *</Label>
                <div className="relative">
                  <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="signup-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupConfirmPassword}
                    onChange={(e) => setSignupConfirmPassword(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white"
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create Account
              </Button>
            </form>

            {/* Google Sign-In temporarily disabled - OAuth not configured in Supabase */}
            {/* <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <Chrome className="mr-2 h-4 w-4" />
              Google
            </Button> */}
          </TabsContent>

          {/* FORGOT PASSWORD TAB */}
          <TabsContent value="forgot" className="space-y-4">
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <div className="relative">
                  <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-3 h-4 w-4 text-muted-foreground`} />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={isRTL ? 'pr-10' : 'pl-10'}
                    disabled={loading}
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  We'll send you a link to reset your password
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white"
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Send Reset Link
              </Button>

              <Button
                type="button"
                variant="link"
                className="w-full"
                onClick={() => setActiveTab("login")}
              >
                Back to Login
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}